/**
 * Batched space/topic name resolution for RAG sources.
 * Chunk metadata stores spaceId/topicId; this resolves them to display names
 * so retrieved context can show which Space and Topic content came from.
 */

import { supabase } from "@/lib/supabase";

export interface SpaceTopicNames {
  spaceNames: Map<string, string>;
  topicNames: Map<string, string>;
}

// Session-scoped cache; space/topic renames are rare enough that stale names are acceptable.
const spaceNameCache = new Map<string, string>();
const topicNameCache = new Map<string, string>();

async function fetchNames(
  table: "mastermind_spaces" | "topics",
  ids: string[],
  cache: Map<string, string>
): Promise<void> {
  const missing = [...new Set(ids)].filter((id) => id && !cache.has(id));
  if (missing.length === 0) return;

  const { data, error } = await supabase
    .from(table)
    .select("id, name")
    .in("id", missing);

  if (error) {
    console.warn(`[spaceTopicNameResolver] ${table} lookup failed:`, error.message);
    return;
  }

  for (const row of (data || []) as Array<{ id: string | number; name: string | null }>) {
    if (row.name) cache.set(String(row.id), row.name);
  }
}

/**
 * Resolve space and topic ids to names. Batched (one query per table) and cached per session.
 * Ids that cannot be resolved are simply absent from the returned maps.
 */
export async function resolveSpaceTopicNames(
  spaceIds: Array<string | null | undefined>,
  topicIds: Array<string | null | undefined>
): Promise<SpaceTopicNames> {
  const cleanSpaceIds = spaceIds.filter((id): id is string => typeof id === "string" && id.length > 0);
  const cleanTopicIds = topicIds.filter((id): id is string => typeof id === "string" && id.length > 0);

  try {
    await Promise.all([
      fetchNames("mastermind_spaces", cleanSpaceIds, spaceNameCache),
      fetchNames("topics", cleanTopicIds, topicNameCache),
    ]);
  } catch (e) {
    console.warn("[spaceTopicNameResolver] resolution failed:", e);
  }

  const spaceNames = new Map<string, string>();
  for (const id of cleanSpaceIds) {
    const name = spaceNameCache.get(id);
    if (name) spaceNames.set(id, name);
  }
  const topicNames = new Map<string, string>();
  for (const id of cleanTopicIds) {
    const name = topicNameCache.get(id);
    if (name) topicNames.set(id, name);
  }

  return { spaceNames, topicNames };
}

/** Test/HMR helper: clear the session cache. */
export function clearSpaceTopicNameCache(): void {
  spaceNameCache.clear();
  topicNameCache.clear();
}
